import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Ticket {
  id: number;
  ticket_id?: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  company_name?: string;
  user_email?: string;
  user_name?: string;
  assigned_to_email?: string;
  is_outage?: number;
}

interface AssignedTask {
  id: number;
  project_id: number;
  title: string;
  description: string | null;
  status: string;
  due_date: string | null;
  project_name: string;
  company_name?: string | null;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    companies: 0,
    services: 0,
    tickets: 0,
    openTickets: 0,
    bookmarks: 0,
    wikis: 0,
  });
  const [dashboardTickets, setDashboardTickets] = useState<Ticket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [assignedTasks, setAssignedTasks] = useState<AssignedTask[]>([]);
  const [loadingAssignedTasks, setLoadingAssignedTasks] = useState(false);
  const [ticketsSectionExpanded, setTicketsSectionExpanded] = useState(true);
  const [tasksSectionExpanded, setTasksSectionExpanded] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (user?.role === 'admin' && !user?.company_id) {
          const [companiesRes, servicesRes, ticketsRes, bookmarksRes] = await Promise.all([
            api.get('/companies'),
            api.get('/services'),
            api.get('/tickets'),
            api.get('/companies/bookmarks/all'),
          ]);

          // Count all wiki documents across all companies (agents can see all)
          let totalWikis = 0;
          try {
            const companies = companiesRes.data;
            const wikiPromises = companies.map((company: any) => 
              api.get(`/wiki/${company.id}/documents`).catch(() => ({ data: [] }))
            );
            const wikiResults = await Promise.all(wikiPromises);
            totalWikis = wikiResults.reduce((sum, res) => sum + res.data.length, 0);
          } catch (error) {
            console.error('Error fetching wiki counts:', error);
          }

          const openTickets = ticketsRes.data.filter((t: any) => 
            t.status === 'open' || t.status === 'pending'
          ).length;

          setStats({
            companies: companiesRes.data.length,
            services: servicesRes.data.length,
            tickets: ticketsRes.data.length,
            openTickets,
            bookmarks: bookmarksRes.data.length,
            wikis: totalWikis,
          });
        } else if (user?.company_id) {
          const [servicesRes, ticketsRes, bookmarksRes, wikisRes] = await Promise.all([
            api.get('/services'), 
            api.get('/tickets'),
            api.get(`/companies/${user.company_id}/bookmarks`),
            api.get(`/wiki/${user.company_id}/documents`).catch(() => ({ data: [] })),
          ]);

          const openTickets = ticketsRes.data.filter((t: any) => 
            t.status === 'open' || t.status === 'pending'
          ).length;

          setStats({
            companies: 1, // Current company
            services: servicesRes.data.length,
            tickets: ticketsRes.data.length,
            openTickets,
            bookmarks: bookmarksRes.data.length,
            wikis: wikisRes.data.length,
          });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, [user]);

  useEffect(() => {
    const fetchDashboardTickets = async () => {
      if (!user) return;
      
      setLoadingTickets(true);
      try {
        const response = await api.get('/tickets/dashboard');
        setDashboardTickets(response.data);
      } catch (error) {
        console.error('Error fetching dashboard tickets:', error);
      } finally {
        setLoadingTickets(false);
      }
    };

    fetchDashboardTickets();
  }, [user]);

  useEffect(() => {
    const fetchAssignedTasks = async () => {
      if (!user || user.company_id != null) return;
      setLoadingAssignedTasks(true);
      try {
        const response = await api.get('/internal/projects/my-tasks');
        setAssignedTasks(response.data || []);
      } catch {
        setAssignedTasks([]);
      } finally {
        setLoadingAssignedTasks(false);
      }
    };
    fetchAssignedTasks();
  }, [user]);

  const isGlobalAdmin = user?.role === 'admin' && !user?.company_id;
  const isGlobalUser = !user?.company_id;
  const isCompanyUser = !!user?.company_id;

  // For Assigned Tickets (global users), hide closed and resolved
  const dashboardTicketsToShow = isCompanyUser
    ? dashboardTickets
    : dashboardTickets.filter((t) => t.status !== 'closed' && t.status !== 'resolved');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-orange-100 text-orange-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {isGlobalAdmin ? 'Admin Dashboard' : 'Company Dashboard'}
      </h1>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
        {isGlobalAdmin && (
          <Link to="/companies" className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Companies</dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.companies}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        )}

        {isCompanyUser && (
          <Link to="/services" className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                      Our Services
                    </dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.services}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        )}

        <Link to="/tickets" className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Tickets</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.tickets}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/tickets?status=open" className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Open Tickets</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.openTickets}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        {isGlobalAdmin && (
          <Link to="/internal/projects" className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Assigned Tasks</dt>
                    <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {loadingAssignedTasks ? '…' : assignedTasks.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </Link>
        )}

        <Link to={user?.company_id ? "/bookmarks" : "/companies"} className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Bookmarks</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.bookmarks}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to={user?.company_id ? "/wiki" : "/companies"} className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {isGlobalAdmin ? 'Total Wikis' : 'Wikis'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">{stats.wikis}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>
      </div>
      
      {/* Assigned Tickets Section */}
      <div className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg">
        <button
          type="button"
          onClick={() => setTicketsSectionExpanded((e) => !e)}
          className="w-full px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isCompanyUser ? 'My Support Tickets' : 'Assigned Tickets'}
          </h2>
          <span className="flex items-center gap-2">
            <Link
              to="/tickets"
              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
              onClick={(e) => e.stopPropagation()}
            >
              View All →
            </Link>
            <svg
              className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${ticketsSectionExpanded ? '' : '-rotate-90'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>
        {ticketsSectionExpanded && (
        <div className="overflow-x-auto">
          {loadingTickets ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading tickets...
            </div>
          ) : dashboardTicketsToShow.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {isCompanyUser 
                ? 'You haven\'t created any support tickets yet.' 
                : 'No tickets are currently assigned to you.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ticket ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company
                  </th>
                  {isCompanyUser && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Priority
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {dashboardTicketsToShow.map((ticket) => {
                  const ticketId = ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`;
                  const isOutage = ticket.is_outage === 1;
                  return (
                    <tr key={ticket.id} className={`${isOutage ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500' : ''} hover:bg-gray-50 dark:hover:bg-gray-700`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-sm font-mono font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline"
                        >
                          {ticketId}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline block"
                        >
                          {ticket.subject}
                        </Link>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{ticket.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {ticket.company_name || 'N/A'}
                      </td>
                      {isCompanyUser && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {ticket.user_name && ticket.user_email 
                            ? `${ticket.user_name} (${ticket.user_email})`
                            : ticket.user_email || 'N/A'}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>

      {/* Assigned Tasks Section (global admin only) */}
      {isGlobalAdmin && (
      <div className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg">
        <button
          type="button"
          onClick={() => setTasksSectionExpanded((e) => !e)}
          className="w-full px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Assigned Tasks
          </h2>
          <span className="flex items-center gap-2">
            <Link
              to="/internal/projects"
              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
              onClick={(e) => e.stopPropagation()}
            >
              View All →
            </Link>
            <svg
              className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform ${tasksSectionExpanded ? '' : '-rotate-90'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>
        {tasksSectionExpanded && (
        <div className="overflow-x-auto">
          {loadingAssignedTasks ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading tasks...
            </div>
          ) : assignedTasks.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No tasks are currently assigned to you.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Task
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Project
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Company
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Due date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {assignedTasks.map((task) => {
                  const taskStatusLabel = task.status === 'done' ? 'Done' : task.status === 'in_progress' ? 'In Progress' : 'To Do';
                  const isOverdue = task.due_date && task.status !== 'done' && new Date() > new Date(task.due_date + 'T23:59:59.999');
                  return (
                    <tr key={task.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <Link
                          to={`/internal/projects/${task.project_id}`}
                          state={{ openTaskId: task.id }}
                          className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline block"
                        >
                          {task.title}
                        </Link>
                        {task.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{task.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/internal/projects/${task.project_id}`}
                          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {task.project_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {task.company_name ?? 'Internal'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          task.status === 'done'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                            : task.status === 'in_progress'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {taskStatusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {task.due_date ? (
                          <span className={isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>
      )}

      {!isGlobalAdmin && (
        <div className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300">Welcome to your company portal. Use the navigation above to manage your services and tickets.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
